import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  ResponsiveContainer, Tooltip as ReTooltip,
} from 'recharts'
import { useCurrency } from '@/contexts/CurrencyContext'
import { compactCurrency } from '@/components/admin/dashboard/dashboardFormat'
import { chartTooltip } from '@/lib/chartTheme'
import type { AdvancedStockReport } from '@/types'
import * as Help from '@/lib/inventory-report-help'
import { MetricHint, StatTile } from './primitives'

/**
 * "What did you use?" — usage (recipe-driven sales depletion + kitchen/staff
 * issues) next to purchases, with a zero-filled bar trend. Every bucket in the
 * window is drawn, quiet days included, so the shape of the chart is the shape
 * of the data — not an interpolation artifact.
 */
export function ActivityCard({ activity }: { activity: AdvancedStockReport['activity'] }) {
  const { formatCurrency } = useCurrency()
  const trend = activity.trend ?? []
  const hasAnyMovement = activity.used_value > 0 || activity.purchased_value > 0

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base font-semibold">What did you use?</CardTitle>
          <MetricHint text={Help.ACTIVITY_USED} label="usage" />
        </div>
        <CardDescription className="text-xs">
          What left your shelves for customers and the kitchen, next to what you bought in.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatTile
            label="Used"
            value={compactCurrency(activity.used_value)}
            hint={Help.ACTIVITY_USED}
            sub={activity.used_value > 0
              ? `${compactCurrency(activity.sold_depletion_value)} via sales · ${compactCurrency(activity.kitchen_issue_value)} kitchen & staff`
              : undefined}
          />
          <StatTile
            label="Purchased"
            value={compactCurrency(activity.purchased_value)}
            hint={Help.ACTIVITY_PURCHASED}
          />
          <StatTile
            label="Days of stock left"
            value={activity.days_of_stock_left == null
              ? '—'
              : activity.days_of_stock_left > 999 ? '999+' : `~${Math.round(activity.days_of_stock_left)}`}
            hint={Help.ACTIVITY_DAYS_LEFT}
            sub={activity.days_of_stock_left == null ? 'No usage recorded yet' : 'At this period’s pace'}
          />
          <StatTile
            label="Of stock value used"
            value={activity.used_pct_of_stock_value == null ? '—' : `${activity.used_pct_of_stock_value.toFixed(0)}%`}
            hint={Help.ACTIVITY_USED_PCT}
          />
        </div>

        {!hasAnyMovement ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            Nothing recorded in this period yet. Usage appears automatically as you sell items with recipes,
            record purchases, or issue stock to the kitchen.
          </p>
        ) : (
          <div>
            <div className="flex items-center gap-1.5 mb-1 text-xs text-muted-foreground">
              Bought vs used, per {activity.trend_granularity === 'week' ? 'week' : 'day'}
              <MetricHint text={Help.ACTIVITY_TREND} label="trend" />
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trend} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis
                  width={64}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(v) => compactCurrency(Number(v))}
                />
                <ReTooltip {...chartTooltip} formatter={(val: number) => formatCurrency(val)} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="purchase_value" name="Bought" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={26} />
                <Bar dataKey="usage_value" name="Used" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
